/**
 * The process calendar — the pure half (bata-reqs issue 38 R2, owner rulings 2026-10-17;
 * bata-platform ADR 2136; this repository's ADR 9068). No React.
 *
 * A market keeps, a year ahead, dated periods of five types. A change to them is a registry change
 * set (`POST /registry/process-calendar/changes`, or an import of the commercial plan) approved in
 * the same queue as every parameter change. Each item is spelled by the platform
 * (`planning.calendar.process`), and this module reads it back the same way:
 *
 * - `targetKey` `process_calendar.<country>.<period id>` — the period id is
 *   `<country>-<type>-<yyyymmdd>[-n]`, so the item never reads as a catalogue key
 *   (`process_calendar.transition.run_ahead_days` has no two-letter market);
 * - `newValue` / `previousValue` `<type>|<start>|<end>|<name>|<mechanic>` (the start and the end
 *   are the period's first and LAST day), or `removed`.
 *
 * **The checks are the platform's, repeated so a planner hears them before sending**: the dates in
 * order, a start strictly after today (never backdated), a transition or outlet window on the 1st, a
 * promotional period named, a mechanic only on a promotional period and only one the market governs.
 * The rules BETWEEN periods (no overlap, contiguous pricing periods, no start in a blackout, the
 * run-ahead) are the platform's alone; a refusal it returns is shown by its `reason`.
 */

import type { TFunction } from 'i18next';

import { ApiProblemError } from '@/api';
import type {
  PendingItemView, PeriodChangeRequest, PeriodValue, PeriodView, ProcessCalendarView, ProposeCalendarRequest,
} from '@/api/generated';

export const PERIOD_TYPES = ['promotional', 'pricing', 'transition', 'outlet', 'blackout'] as const;
export type PeriodType = (typeof PERIOD_TYPES)[number];

export function isPeriodType(value: string): value is PeriodType {
  return (PERIOD_TYPES as readonly string[]).includes(value);
}

/** The types whose window starts on the 1st (the phase register is monthly, ADR 2136 §2). */
export const MONTHLY_WINDOW_TYPES: readonly PeriodType[] = ['transition', 'outlet'];

export const CALENDAR_TARGET_STEM = 'process_calendar';
export const REMOVED_VALUE = 'removed';
const KEY_SEPARATOR = '.';
const VALUE_SEPARATOR = '|';
const MARKET = /^[A-Z]{2}$/;
const PERIOD_ID = /^[A-Z]{2}-[a-z]+-\d{8}(-\d+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** The platform's bound on a period's name and mechanic (`PeriodChangeRequest`). */
export const MAX_TEXT_LENGTH = 200;
/** The register refuses a rationale under eight words (bata-platform `changeset`, `rationale_not_prose`). */
export const MINIMUM_RATIONALE_WORDS = 8;

// ── the change item ──────────────────────────────────────────────────────────

export interface CalendarTarget {
  readonly countryKey: string;
  readonly periodId: string;
}

/** The period a change item writes, or `null` for any other item (a catalogue key included). */
export function parseCalendarTarget(targetKey: string): CalendarTarget | null {
  const prefix = `${CALENDAR_TARGET_STEM}${KEY_SEPARATOR}`;
  if (targetKey.includes(VALUE_SEPARATOR) || !targetKey.startsWith(prefix)) return null;
  const parts = targetKey.slice(prefix.length).split(KEY_SEPARATOR);
  if (parts.length !== 2) return null;
  const [countryKey, periodId] = parts as [string, string];
  if (!MARKET.test(countryKey) || !PERIOD_ID.test(periodId)) return null;
  return { countryKey, periodId };
}

export function isCalendarTarget(targetKey: string): boolean {
  return parseCalendarTarget(targetKey) !== null;
}

export function calendarTargetKey(target: CalendarTarget): string {
  return [CALENDAR_TARGET_STEM, target.countryKey, target.periodId].join(KEY_SEPARATOR);
}

export type CalendarItemValue =
  | { readonly kind: 'removed' }
  | { readonly kind: 'period'; readonly period: PeriodValue };

/** An item's value decoded, or `null` when it is not in the platform's spelling. */
export function parseCalendarValue(raw: string): CalendarItemValue | null {
  if (raw === REMOVED_VALUE) return { kind: 'removed' };
  const parts = raw.split(VALUE_SEPARATOR);
  if (parts.length !== 5) return null;
  const [periodType, startsOn, endsOn, name, mechanic] = parts as [string, string, string, string, string];
  if (!isPeriodType(periodType) || !ISO_DATE.test(startsOn) || !ISO_DATE.test(endsOn)) return null;
  return {
    kind: 'period',
    period: { periodType, startsOn, endsOn, name: name === '' ? null : name, mechanic: mechanic === '' ? null : mechanic },
  };
}

// ── words ────────────────────────────────────────────────────────────────────

type Translate = TFunction;

/** Literal keys, so the catalogue census sees every one of them used. */
export const PERIOD_TYPE_KEY: Readonly<Record<PeriodType, string>> = {
  promotional: 'foundations.calendar.type.promotional',
  pricing: 'foundations.calendar.type.pricing',
  transition: 'foundations.calendar.type.transition',
  outlet: 'foundations.calendar.type.outlet',
  blackout: 'foundations.calendar.type.blackout',
};

export function periodTypeWord(t: Translate, periodType: string): string {
  return isPeriodType(periodType) ? t(PERIOD_TYPE_KEY[periodType]) : periodType;
}

/** A calendar date in the reader's language (UTC: the calendar's dates are days, not instants). */
export function dayFace(iso: string, language: string): string {
  if (!ISO_DATE.test(iso)) return iso;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
}

/** «1 nov 2026 – 30 nov 2026», both days included. */
export function spanFace(from: string, to: string, language: string): string {
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' })
    .formatRange(new Date(`${from}T00:00:00Z`), new Date(`${to}T00:00:00Z`));
}

/** A period as one line: its type, its days, its name and mechanic. */
export function periodFace(t: Translate, period: PeriodValue, language: string): string {
  const type = periodTypeWord(t, period.periodType);
  const span = spanFace(period.startsOn, period.endsOn, language);
  const name = period.name ?? '';
  const mechanic = period.mechanic ?? '';
  if (name === '' && mechanic === '') return t('foundations.calendar.periodFace.plain', { type, span });
  if (mechanic === '') return t('foundations.calendar.periodFace.named', { type, span, name });
  return t('foundations.calendar.periodFace.withMechanic', { type, span, name, mechanic });
}

/** A change item's value as the approval card and the change record show it. */
export function calendarValueFace(t: Translate, raw: string, language: string): string {
  const value = parseCalendarValue(raw);
  if (value === null) return t('foundations.parameters.itemValue.unreadable');
  return value.kind === 'removed' ? t('foundations.calendar.item.removed') : periodFace(t, value.period, language);
}

/** A calendar item's name: the market and the period, in words. */
export function calendarItemName(t: Translate, target: CalendarTarget): string {
  return t('foundations.calendar.item.name', { country: target.countryKey, period: target.periodId });
}

export const ACTION_KEY: Readonly<Record<string, string>> = {
  create: 'foundations.calendar.action.create',
  edit: 'foundations.calendar.action.edit',
  remove: 'foundations.calendar.action.remove',
};

export function actionWord(t: Translate, action: string): string {
  const key = ACTION_KEY[action];
  return key === undefined ? action : t(key);
}

// ── the year, by lane ────────────────────────────────────────────────────────

/** The year's periods, one lane per type, in the platform's order, each in start order. */
export function lanes(view: Pick<ProcessCalendarView, 'periods'>): ReadonlyMap<PeriodType, readonly PeriodView[]> {
  const byType = new Map<PeriodType, PeriodView[]>(PERIOD_TYPES.map((type) => [type, []]));
  for (const period of view.periods) {
    if (isPeriodType(period.periodType)) byType.get(period.periodType)?.push(period);
  }
  for (const periods of byType.values()) periods.sort((a, b) => a.startsOn.localeCompare(b.startsOn));
  return byType;
}

// ── the market's day ─────────────────────────────────────────────────────────

/** The catalogue key that names a market's IANA zone (owner ruling 2026-10-17, bata-platform ADR 2136). */
export const MARKET_TIMEZONE_KEY = 'market.timezone';

/**
 * The calendar date it is in `zone` at `now`, as `YYYY-MM-DD` — the day the platform's never-backdated
 * and locked checks use. `null` for a zone the browser does not know.
 */
export function todayIn(zone: string, now: Date): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = (type: string): string => parts.find((each) => each.type === type)?.value ?? '';
    const day = [part('year'), part('month'), part('day')].join('-');
    return ISO_DATE.test(day) ? day : null;
  } catch {
    return null;
  }
}

// ── pricing coverage (owner, 2026-10-17) ─────────────────────────────────────

/** The platform's coverage warnings (`planning.calendar.process`), each a sentence key. */
export const COVERAGE_WARNING_KEY: Readonly<Record<string, string>> = {
  pricing_coverage_short: 'foundations.calendar.coverage.short',
  stay_not_governed: 'foundations.calendar.coverage.stayNotGoverned',
};

/** How the coverage reads: covered, short (to a day, or not at all), the stay not governed. Pure. */
export type CoverageKind = 'covered' | 'short' | 'uncovered' | 'stayNotGoverned';

export function coverageKind(coverage: { readonly warning?: string | null; readonly coveredTo?: string | null }): CoverageKind {
  if (coverage.warning === 'stay_not_governed') return 'stayNotGoverned';
  if (coverage.warning === 'pricing_coverage_short') return coverage.coveredTo === null || coverage.coveredTo === undefined ? 'uncovered' : 'short';
  return 'covered';
}

// ── overlapping promotions (owner, 2026-10-17) ───────────────────────────────

/**
 * Promotional periods may overlap each other (and only they): for each promotional period, the
 * others whose days it shares, in start order. Every other type is refused an overlap by the
 * platform, so none is drawn as one. Pure.
 */
export function promotionalOverlaps(periods: readonly Pick<PeriodView, 'periodId' | 'periodType' | 'startsOn' | 'endsOn'>[]): ReadonlyMap<string, readonly string[]> {
  const promotions = periods.filter((period) => period.periodType === 'promotional')
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn));
  const overlaps = new Map<string, string[]>();
  for (const one of promotions) {
    const others = promotions.filter((other) => other.periodId !== one.periodId
      && other.startsOn <= one.endsOn && one.startsOn <= other.endsOn).map((other) => other.periodId);
    if (others.length > 0) overlaps.set(one.periodId, others);
  }
  return overlaps;
}

/** The years a calendar is opened on: last year to two years ahead. */
export function yearChoices(today: string): readonly number[] {
  const year = Number(today.slice(0, 4));
  return [year - 1, year, year + 1, year + 2];
}

// ── the editor ───────────────────────────────────────────────────────────────

/** One change as the editor holds it: what the form says, before the platform checks it. */
export interface PeriodChangeDraft {
  readonly action: 'create' | 'edit' | 'remove';
  /** The period an edit or a removal names; absent for a create. */
  readonly periodId?: string;
  readonly periodType: PeriodType;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly name: string;
  readonly mechanic: string;
}

export interface CalendarProposalDraft {
  readonly countryKey: string;
  readonly changes: readonly PeriodChangeDraft[];
  readonly owner: string;
  readonly rationale: string;
  readonly reviewDueOn: string;
  readonly evidenceRef: string;
}

/** What the editor refuses before sending — the platform's reason where it has one. */
export type DraftRefusal =
  | 'window_not_ordered'
  | 'window_start_not_first_of_month'
  | 'name_required'
  | 'mechanic_not_permitted'
  | 'mechanic_not_governed'
  | 'text_malformed'
  | 'text_too_long'
  | 'starts_before_tomorrow'
  | 'date_missing';

export type ProposalRefusal =
  | DraftRefusal
  | 'no_change'
  | 'no_owner'
  | 'no_rationale'
  | 'rationale_too_short'
  | 'review_not_after_today';

/**
 * The first rule one change breaks, or `null`. `mechanics` is the market's governed vocabulary,
 * `null` when none is in force (so no mechanic may be stated). A removal states nothing to check.
 */
export function checkChange(change: PeriodChangeDraft, today: string, mechanics: readonly string[] | null): DraftRefusal | null {
  if (change.action === 'remove') return null;
  if (!ISO_DATE.test(change.startsOn) || !ISO_DATE.test(change.endsOn)) return 'date_missing';
  if (change.endsOn < change.startsOn) return 'window_not_ordered';
  if (change.startsOn <= today) return 'starts_before_tomorrow';
  if (MONTHLY_WINDOW_TYPES.includes(change.periodType) && !change.startsOn.endsWith('-01')) return 'window_start_not_first_of_month';
  const name = change.name.trim();
  const mechanic = change.mechanic.trim();
  if (name.length > MAX_TEXT_LENGTH || mechanic.length > MAX_TEXT_LENGTH) return 'text_too_long';
  if (name.includes(VALUE_SEPARATOR) || mechanic.includes(VALUE_SEPARATOR)) return 'text_malformed';
  if (change.periodType === 'promotional' && name === '') return 'name_required';
  if (mechanic !== '') {
    if (change.periodType !== 'promotional') return 'mechanic_not_permitted';
    if (mechanics === null || !mechanics.includes(mechanic)) return 'mechanic_not_governed';
  }
  return null;
}

function words(text: string): number {
  return text.trim().split(/\s+/).filter((word) => word !== '').length;
}

/** One draft as the request states it: a removal names its period and nothing else. */
export function changeRequest(change: PeriodChangeDraft): PeriodChangeRequest {
  if (change.action === 'remove') return { action: 'remove', periodId: change.periodId ?? null };
  const name = change.name.trim();
  const mechanic = change.mechanic.trim();
  return {
    action: change.action,
    ...(change.action === 'edit' ? { periodId: change.periodId ?? null } : { periodType: change.periodType }),
    startsOn: change.startsOn,
    endsOn: change.endsOn,
    name: name === '' ? null : name,
    mechanic: mechanic === '' ? null : mechanic,
  };
}

export type CalendarComposition =
  | { readonly ok: true; readonly body: ProposeCalendarRequest }
  | { readonly ok: false; readonly refusal: ProposalRefusal; readonly changeIndex: number | null };

/** The proposal, or the first refusal (a change's own, by its place in the set). */
export function composeCalendarProposal(
  draft: CalendarProposalDraft, changeId: string, today: string, mechanics: readonly string[] | null,
): CalendarComposition {
  const refuse = (refusal: ProposalRefusal, changeIndex: number | null = null): CalendarComposition => (
    { ok: false, refusal, changeIndex }
  );
  if (draft.changes.length === 0) return refuse('no_change');
  for (const [index, change] of draft.changes.entries()) {
    const refusal = checkChange(change, today, mechanics);
    if (refusal !== null) return refuse(refusal, index);
  }
  if (!ISO_DATE.test(draft.reviewDueOn) || draft.reviewDueOn <= today) return refuse('review_not_after_today');
  if (draft.owner.trim() === '') return refuse('no_owner');
  if (draft.rationale.trim() === '') return refuse('no_rationale');
  if (words(draft.rationale) < MINIMUM_RATIONALE_WORDS) return refuse('rationale_too_short');
  return {
    ok: true,
    body: {
      changeId,
      countryKey: draft.countryKey,
      owner: draft.owner.trim(),
      rationale: draft.rationale.trim(),
      reviewDueOn: draft.reviewDueOn,
      changes: draft.changes.map(changeRequest),
      ...(draft.evidenceRef.trim() === '' ? {} : { evidenceRef: draft.evidenceRef.trim() }),
    },
  };
}

/** A new period's form, empty but for its type. */
export function blankChange(periodType: PeriodType): PeriodChangeDraft {
  return { action: 'create', periodType, startsOn: '', endsOn: '', name: '', mechanic: '' };
}

/** An edit's form, starting from the period as it stands. */
export function editOf(period: PeriodView): PeriodChangeDraft {
  return {
    action: 'edit',
    periodId: period.periodId,
    periodType: isPeriodType(period.periodType) ? period.periodType : 'pricing',
    startsOn: period.startsOn,
    endsOn: period.endsOn,
    name: period.name ?? '',
    mechanic: period.mechanic ?? '',
  };
}

export function removalOf(period: PeriodView): PeriodChangeDraft {
  return { ...editOf(period), action: 'remove' };
}

/** A draft as the pending list and the tray show it, before and after. */
export function draftItem(change: PeriodChangeDraft, current: PeriodView | null): PendingItemView {
  const before: PeriodValue | null = current === null ? null : {
    periodType: current.periodType, startsOn: current.startsOn, endsOn: current.endsOn,
    name: current.name ?? null, mechanic: current.mechanic ?? null,
  };
  const after: PeriodValue | null = change.action === 'remove' ? null : {
    periodType: change.periodType, startsOn: change.startsOn, endsOn: change.endsOn,
    name: change.name.trim() === '' ? null : change.name.trim(),
    mechanic: change.mechanic.trim() === '' ? null : change.mechanic.trim(),
  };
  return { periodId: change.periodId ?? '', action: change.action, before, after };
}

// ── the platform's refusals ──────────────────────────────────────────────────

export const CALENDAR_INVALID_CODE = 'platform.registry.process_calendar_invalid';
export const CALENDAR_LOCKED_CODE = 'platform.registry.process_calendar_period_locked';
export const CALENDAR_CHANGED_CODE = 'platform.registry.process_calendar_period_changed';
export const WORKBOOK_UNREADABLE_CODE = 'platform.registry.process_calendar_workbook_unreadable';
const CALENDAR_CODES: readonly string[] = [CALENDAR_INVALID_CODE, CALENDAR_LOCKED_CODE, CALENDAR_CHANGED_CODE, WORKBOOK_UNREADABLE_CODE];

export interface CalendarRefusal {
  readonly code: string;
  readonly reason: string;
  readonly periodId: string | null;
  /** The sheet row of an imported row (1 = the header). */
  readonly row: number | null;
  /** The sheet column of an unreadable cell. */
  readonly column: string | null;
}

/** A calendar refusal's members, or `null` for any other failure. */
export function calendarRefusal(error: unknown): CalendarRefusal | null {
  if (!(error instanceof ApiProblemError) || !CALENDAR_CODES.includes(error.problem.code)) return null;
  const member = (name: string): unknown => error.problem[name];
  const text = (name: string): string | null => {
    const value = member(name);
    return typeof value === 'string' && value !== '' ? value : null;
  };
  const row = member('row');
  const column = member('column');
  return {
    code: error.problem.code,
    reason: text('reason') ?? '',
    periodId: text('periodId'),
    row: typeof row === 'number' ? row : null,
    column: typeof column === 'number' ? String(column) : text('column'),
  };
}

/**
 * Every reason the platform names (`planning.calendar.process`, the workbook reader) and every one
 * the editor names first, spelled out so the catalogue check sees each key. A reason not listed is
 * still said — `foundations.calendar.reason.unknown`, with the code.
 */
export const REASON_KEY: Readonly<Record<string, string>> = {
  period_type_unknown: 'foundations.calendar.reason.period_type_unknown',
  window_not_ordered: 'foundations.calendar.reason.window_not_ordered',
  window_start_not_first_of_month: 'foundations.calendar.reason.window_start_not_first_of_month',
  periods_overlap: 'foundations.calendar.reason.periods_overlap',
  pricing_periods_not_contiguous: 'foundations.calendar.reason.pricing_periods_not_contiguous',
  starts_in_blackout: 'foundations.calendar.reason.starts_in_blackout',
  name_required: 'foundations.calendar.reason.name_required',
  mechanic_not_permitted: 'foundations.calendar.reason.mechanic_not_permitted',
  mechanic_not_governed: 'foundations.calendar.reason.mechanic_not_governed',
  text_malformed: 'foundations.calendar.reason.text_malformed',
  starts_before_tomorrow: 'foundations.calendar.reason.starts_before_tomorrow',
  starts_inside_run_ahead: 'foundations.calendar.reason.starts_inside_run_ahead',
  period_locked: 'foundations.calendar.reason.period_locked',
  period_unknown: 'foundations.calendar.reason.period_unknown',
  period_repeated: 'foundations.calendar.reason.period_repeated',
  period_type_changed: 'foundations.calendar.reason.period_type_changed',
  action_unknown: 'foundations.calendar.reason.action_unknown',
  period_changed_since_proposal: 'foundations.calendar.reason.period_changed_since_proposal',
  market_malformed: 'foundations.calendar.reason.market_malformed',
  market_unknown: 'foundations.calendar.reason.market_unknown',
  text_too_long: 'foundations.calendar.reason.text_too_long',
  row_outside_year: 'foundations.calendar.reason.row_outside_year',
  row_repeated: 'foundations.calendar.reason.row_repeated',
  item_unreadable: 'foundations.calendar.reason.item_unreadable',
  // The market's day (owner ruling 2026-10-17): no `market.timezone` row, or a zone the platform does not know.
  market_timezone_not_set: 'foundations.calendar.reason.market_timezone_not_set',
  market_timezone_unknown: 'foundations.calendar.reason.market_timezone_unknown',
  // The workbook reader's.
  not_a_workbook: 'foundations.calendar.reason.not_a_workbook',
  workbook_too_large: 'foundations.calendar.reason.workbook_too_large',
  workbook_declares_entities: 'foundations.calendar.reason.workbook_declares_entities',
  no_calendar_sheet: 'foundations.calendar.reason.no_calendar_sheet',
  headers_missing: 'foundations.calendar.reason.headers_missing',
  too_many_rows: 'foundations.calendar.reason.too_many_rows',
  date_unreadable: 'foundations.calendar.reason.date_unreadable',
  not_base64: 'foundations.calendar.reason.not_base64',
  // The editor's own, before sending.
  date_missing: 'foundations.calendar.reason.date_missing',
  no_change: 'foundations.calendar.reason.no_change',
  no_owner: 'foundations.calendar.reason.no_owner',
  no_rationale: 'foundations.calendar.reason.no_rationale',
  rationale_too_short: 'foundations.calendar.reason.rationale_too_short',
  review_not_after_today: 'foundations.calendar.reason.review_not_after_today',
  no_file: 'foundations.calendar.reason.no_file',
};

export const UNKNOWN_REASON_KEY = 'foundations.calendar.reason.unknown';

/** A reason's sentence key, or the unknown reason's. */
export function reasonKey(reason: string): string {
  return REASON_KEY[reason] ?? UNKNOWN_REASON_KEY;
}

/** Where a refusal points: a period, a sheet row and column, a sheet row, or nowhere. */
export function refusalPlace(t: Translate, refusal: CalendarRefusal): string | null {
  if (refusal.row !== null && refusal.column !== null) return t('foundations.calendar.refusal.atCell', { row: refusal.row, column: refusal.column });
  if (refusal.row !== null) return t('foundations.calendar.refusal.atRow', { row: refusal.row });
  if (refusal.periodId !== null) return t('foundations.calendar.refusal.atPeriod', { period: refusal.periodId });
  return null;
}

// ── the workbook ─────────────────────────────────────────────────────────────

/** The platform's bound on the uploaded workbook (ADR 2136 §7: 2 MiB in). */
export const MAX_WORKBOOK_BYTES = 2 * 1024 * 1024;

/** Bytes as base64 text, in chunks so a 2 MiB workbook does not overflow the call stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let at = 0; at < bytes.length; at += chunk) {
    binary += String.fromCharCode(...bytes.subarray(at, at + chunk));
  }
  return btoa(binary);
}

/** Base64 text as bytes (the template the platform sends). */
export function base64ToBytes(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let at = 0; at < binary.length; at += 1) bytes[at] = binary.charCodeAt(at);
  return bytes;
}
